import { useEffect, useState } from 'react'
import { StepUpSheet } from '../components/StepUpSheet'
import { StepUpBanner } from '../components/StepUpBanner'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { I18nextProvider } from 'react-i18next'
import { useTheme, AiApiProvider } from '@foroba/ui'
import { aiApi } from '../lib/aiApi'
import { Capacitor } from '@capacitor/core'
import { resetViewportAfterKeyboard } from '../lib/viewport'
import { TabBar } from '../navigation/TabBar'
import { SubNavPills } from '../navigation/SubNavPills'
import { MoreSheet } from '../navigation/MoreSheet'
import { tabsFor, moreItemsFor, PAZURU_SUBNAV, PORTFOLIO_SUBNAV } from '../navigation/navConfig'
import { FLAVOR } from '../flavor/flavor'
import { AuthProvider, useAuth } from '../auth/AuthProvider'
import { InstallBanner } from '../components/InstallBanner'
import { OfflineBanner } from '../components/OfflineBanner'
import { Topbar } from '../components/Topbar'
import { getAccessToken, getBiometricEnabled, clearTokens } from '../lib/pfhApi'
import { authenticate } from '../native/biometric'
import i18n from '../i18n'
import styles from './RootLayout.module.css'

// A declined step-up (428) must not be retried: each retry would re-open the verification sheet
// right after the user dismissed it with "Not now".
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) =>
        failureCount < 1 && !/step-up|428/i.test(error instanceof Error ? error.message : String(error)),
    },
  },
})

/**
 * Biometric gate (#35 — IOS-4.2).
 *
 * Shown at app launch when the user has an existing token AND has enrolled
 * biometrics. Triggers Face ID / Touch ID immediately on mount; on success
 * dismisses itself; on failure clears tokens and redirects to /login.
 */
function BiometricGate({ onUnlock }: { onUnlock: () => void }) {
  const navigate = useNavigate()

  useEffect(() => {
    void (async () => {
      const ok = await authenticate('Confirm your identity to continue')
      if (ok) {
        onUnlock()
      } else {
        // Biometric failed or cancelled — clear session and force re-login
        clearTokens()
        await navigate('/login', { replace: true })
      }
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100dvh',
        background: 'var(--color-bg-primary)',
        color: 'var(--color-text-secondary, #94a3b8)',
        fontSize: '14px',
      }}
    >
      Confirming identity…
    </div>
  )
}

function Shell() {
  useTheme()
  const { user, loading } = useAuth()
  const loc = useLocation()
  const navigate = useNavigate()
  const isLogin = loc.pathname === '/login'

  // Redirect unauthenticated users to login
  useEffect(() => {
    if (!loading && !user && !isLogin) {
      void navigate('/login', { replace: true })
    }
  }, [user, loading, isLogin, navigate])

  // Signing in unmounts the focused field while the keyboard is open; restore the scroll offset iOS left behind.
  const signedIn = user !== null
  useEffect(() => {
    if (signedIn) resetViewportAfterKeyboard()
  }, [signedIn])

  // Biometric lock: true = locked (need biometric), false = unlocked/skip
  const [biometricLocked, setBiometricLocked] = useState<boolean>(
    // Lock on app open if native + token exists + biometric enrolled
    Capacitor.isNativePlatform() &&
    getAccessToken() !== null &&
    getBiometricEnabled(),
  )

  const [moreOpen, setMoreOpen] = useState(false)

  const PAZURU_PATHS = ['/accounts', '/transactions', '/recurring', '/spending', '/forecasts', '/budgets', '/goals', '/debts', '/pazuru/lab', '/pazuru/import-log']
  const PORTFOLIO_PATHS = ['/portfolio', '/etf', '/crypto', '/analysis', '/backtest', '/screener', '/compare', '/sentiment', '/tsumi/lab']

  const subnav =
    PAZURU_PATHS.some((p) => loc.pathname.startsWith(p)) ? PAZURU_SUBNAV
    : PORTFOLIO_PATHS.some((p) => loc.pathname.startsWith(p)) ? PORTFOLIO_SUBNAV
    : null

  if (biometricLocked) {
    return <BiometricGate onUnlock={() => setBiometricLocked(false)} />
  }

  return (
    <div className={styles.shell}>
      <InstallBanner />
      <OfflineBanner />
      {user && !isLogin && <Topbar />}
      {user && !isLogin && subnav && <SubNavPills items={subnav} />}
      {user && !isLogin && <StepUpBanner onVerified={() => void queryClient.invalidateQueries()} />}
      <main className={styles.content}>
        <Outlet />
      </main>

      {user && !isLogin && (
        <>
          <TabBar items={tabsFor(FLAVOR.id)} onOpenMore={() => setMoreOpen(true)} />
          <MoreSheet items={moreItemsFor(FLAVOR.id)} open={moreOpen} onClose={() => setMoreOpen(false)} />
        </>
      )}
      <StepUpSheet />
    </div>
  )
}

export function RootLayout() {
  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <AiApiProvider api={aiApi}>
          <AuthProvider>
            <Shell />
          </AuthProvider>
        </AiApiProvider>
      </QueryClientProvider>
    </I18nextProvider>
  )
}
